import { useEffect, useState } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import './Docs.css';

interface DocEntry {
  slug: string;
  title: string;
  path: string;
}

export default function Docs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [docs, setDocs] = useState<DocEntry[] | null>(null);
  const [indexError, setIndexError] = useState<string | null>(null);
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  /** Goes up by one each time the person asks for the open doc again. */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        setIndexError(null);
        const res = await fetch('/portal-docs/index.json', { cache: 'no-cache' });
        if (!res.ok) throw new Error(`the server answered ${res.status}`);
        const data: DocEntry[] = await res.json();
        setDocs(data);
      } catch (e: unknown) {
        setDocs([]);
        setIndexError(e instanceof Error && e.message ? e.message : 'unknown error');
      }
    })();
  }, []);

  // The open doc lives in the address, so a doc can be linked to. With none named, the first opens.
  const slug = searchParams.get('doc');
  const selected = docs?.find((d) => d.slug === slug) ?? docs?.[0] ?? null;
  const selectedPath = selected?.path;

  useEffect(() => {
    if (!selectedPath) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setContent('');
        const res = await fetch(selectedPath, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`the server answered ${res.status}`);
        const text = await res.text();
        if (!cancelled) setContent(text);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error && e.message ? e.message : 'unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPath, attempt]);

  return (
    <div className="docs-page">
      <PageHeader title="Docs" />

      {indexError && <Alert variant="danger">The list of docs did not load: {indexError}. Reload the page.</Alert>}

      <div className="docs-layout">
        <nav className="docs-nav" aria-label="Docs">
          {docs === null && (
            <div className="docs-nav__state">
              <Spinner animation="border" size="sm" /> Loading docs
            </div>
          )}
          {docs !== null && docs.length === 0 && !indexError && <p className="docs-nav__state">No docs yet.</p>}
          {docs && docs.length > 0 && (
            <ul className="docs-nav__list">
              {docs.map((d) => (
                <li key={d.slug}>
                  <button
                    type="button"
                    className={`docs-nav__item${selected?.slug === d.slug ? ' is-active' : ''}`}
                    aria-current={selected?.slug === d.slug ? 'page' : undefined}
                    onClick={() => setSearchParams({ doc: d.slug })}
                  >
                    {d.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </nav>

        <Card className="docs-doc">
          {error && (
            <Alert variant="danger" className="docs-doc__error">
              <span>
                {selected?.title ?? 'This doc'} did not load: {error}.
              </span>
              <Button variant="outline-danger" size="sm" onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </Button>
            </Alert>
          )}
          {loading && (
            <div className="docs-doc__state">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading {selected?.title}
            </div>
          )}
          {!loading && !error && selected && content.trim() === '' && (
            <div className="docs-doc__state">This doc is empty.</div>
          )}
          {!loading && !error && content.trim() !== '' && (
            <article className="docs-article">
              <ReactMarkdown>{content}</ReactMarkdown>
            </article>
          )}
          {!loading && !error && !selected && docs !== null && (
            <div className="docs-doc__state">There is no doc to show.</div>
          )}
        </Card>
      </div>
    </div>
  );
}
